import React, { useEffect, useRef, useState } from 'react';
import { Button, message } from 'antd';
import { CheckCircleFilled, CopyOutlined, ShareAltOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import { VisitorPassCreationController } from 'fragments/passcodes/fragments/visitor-pass-creation/interfaces';
import { useTranslator } from 'tools/view-hooks/translator-hook';

export const VisitorPassSuccess: React.FC<{ controller: VisitorPassCreationController }> = ({ controller }) => {
    const { translate } = useTranslator();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [copied, setCopied] = useState(false);
    const created = controller.created;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && created?.qr_payload) {
            QRCode.toCanvas(canvas, created.qr_payload, {
                width: 544, // backing-store alto (retina); el tamaño visual lo fijamos en el .then()
                margin: 4, // quiet zone: requerida para que el lector detecte los patrones de posición
                errorCorrectionLevel: 'L', // pantalla (no impreso) => L reduce módulos 69x69 -> 61x61
                color: { dark: '#000000', light: '#ffffff' }, // negro puro: máximo contraste
            })
                .then(() => {
                    // qrcode pisa canvas.style.width/height con el backing size (544px). Lo re-fijamos
                    // con la misma expresión para width y height => cuadrado por construcción + responsivo.
                    canvas.style.width = 'min(240px, 68vw)';
                    canvas.style.height = 'min(240px, 68vw)';
                })
                .catch(() => undefined);
        }
    }, [created]);

    if (!created) {
        return null;
    }

    const onCopy = () => {
        navigator.clipboard?.writeText(created.share_url).then(() => {
            setCopied(true);
            message.success(translate({ key: 'visit-pass.copied' }));
            setTimeout(() => setCopied(false), 2500);
        });
    };

    return (
        <div className={'vp-creation'}>
            <div className={'vp-creation-success'}>
                <CheckCircleFilled className={'vp-creation-check'} aria-hidden />
                <div className={'vp-creation-success-title'}>{translate({ key: 'visit-pass.success-title' })}</div>
                <div className={'vp-creation-success-sub'}>{translate({ key: 'visit-pass.success-sub' })}</div>

                <div className={'vp-creation-card'}>
                    <canvas
                        ref={canvasRef}
                        className={'vp-creation-qr'}
                        width={544}
                        height={544}
                        style={{ width: 'min(240px, 68vw)', height: 'min(240px, 68vw)' }}
                    />
                    <div className={'vp-creation-visitor'}>{controller.visitorName.trim()}</div>
                    <div className={'vp-creation-valid'}>{controller.validityLabel}</div>
                </div>

                <div className={'vp-creation-linkrow'}>
                    <span className={'vp-creation-link'}>{created.share_url}</span>
                    <button
                        className={'vp-creation-copy'}
                        aria-label={translate({ key: 'visit-pass.copy' })}
                        onClick={onCopy}
                    >
                        <CopyOutlined aria-hidden />
                    </button>
                </div>

                <Button
                    type={'primary'}
                    className={'vp-creation-share'}
                    icon={<ShareAltOutlined />}
                    onClick={controller.onShare}
                >
                    {translate({ key: 'visit-pass.share' })}
                </Button>
                <Button type={'link'} className={'vp-creation-again'} onClick={controller.reset}>
                    {translate({ key: 'visit-pass.create-another' })}
                </Button>
                {copied && <span style={{ display: 'none' }} data-testid="copied" />}
            </div>
        </div>
    );
};
